import React, {useState, useEffect, useRef} from "react";
import {observer} from "mobx-react-lite";
import moment from "moment";
import ShiftScoreModal from "../../shiftScore/Components/ShiftScoreModal/ShiftScoreModal";
import {useFetch} from "hooks/useFetch";
import useIsFocused from "hooks/useIsFocused";
import stores from "store/stores";
import {CustomerObjectionsModal} from "./customerObjectionsModal/CustomerObjectionsModal";
import ActiveShifts from "./components/ActiveShifts";
import PastShifts from "./components/PastShifts";
import FutureShifts from "./components/FutureShifts";
import NavigationButtons from "./components/NavigationButtons";
import {Col, Row} from "antd";
import {CustomerHomeMapComponent} from "./components/CustomerHomeMapComponent";
import {
    CustomerHomePageButtonContainer,
    CustomerHomePageMapContainer,
    CustomerNavigationContainer
} from "./CustomerHomePageWithShiftScore.styles";

const CustomerHomePageWithShiftScore = observer(() => {
    const [currentPage, setCurrentPage] = useState(1);
    const [futureCurrentPage, setFutureCurrentPage] = useState(1);
    const [reloadCount, setReloadCount] = useState(false);
    const [applyWaitingApproveFilters, setApplyWaitingApproveFilters] = useState(false);
    const [isWaitingApproveActive, setIsWaitingApproveActive] = useState(false);
    const [reloadData, setReloadData] = useState({});
    const [recall, setRecall] = useState(true);
    const [tabKey, setTabKey] = useState("activeShifts");
    const isFocused = useIsFocused();
    const [isObjectionsModalOpen, setIsObjectionsModalOpen] = useState(false);
    const selectedSubcompanyId = stores?.meStore?.selectedSubCompanyId;
    const hasMultipleSubcompanies = stores?.meStore?.subcompanies?.length > 1;

    const recallRef = useRef(recall);
    recallRef.current = recall;

    const handleObjectionsCancel = () => {
        setIsObjectionsModalOpen(false);
    };

    useEffect(() => {
        if (isFocused) {
            let timeout = null;

            timeout = setTimeout(async () => {
                await setRecall(!recallRef.current);
            }, 30000);

            return () => clearTimeout(timeout);
        }
    }, [recall, isFocused]);

    useEffect(() => {
        if (applyWaitingApproveFilters) {
            setIsWaitingApproveActive(true);
            setTabKey("activeShifts");
            setApplyWaitingApproveFilters(false);
        }
    }, [applyWaitingApproveFilters]);

    useEffect(() => {
        if (selectedSubcompanyId) {
            setFilters(prev => ({
                ...prev,
                subCompany: [selectedSubcompanyId]
            }));
            setOriginalActiveFilters(prev => ({
                ...prev,
                subCompany: [selectedSubcompanyId]
            }));
            setPastShiftsFilters(prev => ({
                ...prev,
                subCompany: [selectedSubcompanyId]
            }));
            setFutureShiftsFilters(prev => ({
                ...prev,
                subCompany: [selectedSubcompanyId]
            }));
        }
    }, [selectedSubcompanyId]);

    //Aktif vardiyalar: bugün sabah 04:00'den şu ana kadar olanlar ve önceden onay bekleyen durumda kalanlar
    const [filters, setFilters] = useState({
        company: undefined,
        subCompany: [selectedSubcompanyId] || undefined,
        period: "today",
        startDate: moment()
            .startOf("day")
            .add(4, "hours")
            .toDate(),
        endDate: moment().toDate(),
        status: "all",
        city: "all",
        currentStatus: "all"
    });

    // Orijinal aktif vardiya filtreleri (onay bekliyor durumunda değişmez)
    const [originalActiveFilters, setOriginalActiveFilters] = useState({
        company: undefined,
        subCompany: [selectedSubcompanyId] || undefined,
        period: "today",
        startDate: moment()
            .startOf("day")
            .add(4, "hours")
            .toDate(),
        endDate: moment().toDate(),
        status: "all",
        city: "all",
        currentStatus: "all"
    });

    //Geçmiş vardiyalar: sabah 04:00 öncesi
    const [pastShiftsFilters, setPastShiftsFilters] = useState({
        company: undefined,
        subCompany: [selectedSubcompanyId] || undefined,
        period: "",
        startDate: "",
        endDate: moment()
            .startOf("day")
            .add(4, "hours")
            .toDate(),
        status: "all",
        city: "all",
        currentStatus: "all"
    });

    const [futureShiftsFilters, setFutureShiftsFilters] = useState({
        company: undefined,
        subCompany: [selectedSubcompanyId] || undefined,
        period: "today",
        startDate: moment()
            .subtract(1, "d")
            .toDate(),
        endDate: moment().toDate(),
        city: "all",
        currentStatus: "all"
    });

    const [loading, data, setData, setLoading] = useFetch({
        fetchFn: stores.shiftStore.getShiftScoreList.bind(null, {
            limit: 10,
            skip: (currentPage - 1) * 10,
            companies: filters.company ? [filters.company] : ["all"],
            subcompanies: filters.subCompany,
            period: filters.period,
            startDate: filters.startDate,
            endDate: filters.endDate,
            status: filters.status,
            city: filters.city,
            currentStatus: filters.currentStatus
        }),
        dependencies: [filters, currentPage, reloadData, recall, selectedSubcompanyId]
    });

    const [, originalActiveData, ,] = useFetch({
        fetchFn: stores.shiftStore.getShiftScoreList.bind(null, {
            limit: 1,
            skip: 0,
            companies: originalActiveFilters.company ? [originalActiveFilters.company] : ["all"],
            subcompanies: originalActiveFilters.subCompany,
            period: originalActiveFilters.period,
            startDate: originalActiveFilters.startDate,
            endDate: originalActiveFilters.endDate,
            status: originalActiveFilters.status,
            city: originalActiveFilters.city,
            currentStatus: originalActiveFilters.currentStatus
        }),
        dependencies: [originalActiveFilters, reloadData, recall, selectedSubcompanyId]
    });

    const [pastLoading, pastData, setPastData, setPastLoading] = useFetch({
        fetchFn: stores.shiftStore.getShiftScoreList.bind(null, {
            limit: 10,
            skip: (currentPage - 1) * 10,
            companies: pastShiftsFilters.company ? [pastShiftsFilters.company] : ["all"],
            subcompanies: pastShiftsFilters.subCompany,
            period: pastShiftsFilters.period,
            startDate: pastShiftsFilters.startDate,
            endDate: pastShiftsFilters.endDate,
            status: pastShiftsFilters.status,
            city: pastShiftsFilters.city,
            currentStatus: pastShiftsFilters.currentStatus
        }),
        dependencies: [
            pastShiftsFilters,
            currentPage,
            reloadData,
            recall,
            selectedSubcompanyId,
            filters
        ]
    });

    const [futureLoading, futureData, setFutureData, setFutureLoading] = useFetch({
        fetchFn: stores.shiftStore.getFutureShiftsList.bind(null, {
            limit: 10,
            skip: (futureCurrentPage - 1) * 10,
            companies: futureShiftsFilters.company ? [futureShiftsFilters.company] : ["all"],
            subcompanies: futureShiftsFilters.subCompany,
            period: futureShiftsFilters.period,
            startDate: futureShiftsFilters.startDate,
            endDate: futureShiftsFilters.endDate,
            city: futureShiftsFilters.city,
            currentStatus: futureShiftsFilters.currentStatus
        }),
        dependencies: [
            futureShiftsFilters,
            futureCurrentPage,
            reloadData,
            recall,
            selectedSubcompanyId
        ]
    });

    const [, count, ,] = useFetch({
        defaultDataValue: 0,
        initialCall: true,
        fetchFn: stores.shiftStore.getWaitingApproveCount.bind(null, {}),
        dependencies: [reloadData, recall, reloadCount],
        condition: stores.meStore.checkRole("isCompanyUser")
    });

    const [, objectionsCount, ,] = useFetch({
        defaultDataValue: 0,
        initialCall: true,
        fetchFn: stores.shiftStore.getObjectionsCount.bind(null, {
            limit: 1,
            skip: 0,
            status: "waitingApprove"
        }),
        dependencies: [reloadData, recall, reloadCount]
    });

    const [isModalOpen, setIsModalOpen] = useState(false);

    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };
    const changeTab = value => {
        setTabKey(value);
        setCurrentPage(1);
        setFutureCurrentPage(1);
        setIsModalOpen(false);

        setIsWaitingApproveActive(false);

        // Aktif vardiyalar tabına geçildiğinde filtreleri sıfırla
        if (value === "activeShifts") {
            setFilters(prev => ({
                ...prev,
                subCompany: [selectedSubcompanyId] || undefined,
                status: "all",
                period: "today",
                startDate: moment()
                    .startOf("day")
                    .add(4, "hours")
                    .toDate(),
                endDate: moment().toDate(),
                currentStatus: "all"
            }));
        } else if (value === "pastShifts") {
            setPastShiftsFilters(prev => ({
                ...prev,
                status: "all",
                period: "",
                startDate: "",
                endDate: moment()
                    .startOf("day")
                    .add(4, "hours")
                    .toDate(),
                currentStatus: "all"
            }));
        } else if (value === "futureShifts") {
            setFutureShiftsFilters(prev => ({
                ...prev,
                status: "all",
                period: "today",
                startDate: moment()
                    .subtract(1, "d")
                    .toDate(),
                endDate: moment().toDate(),
                currentStatus: "all"
            }));
        }
    };
    const onSubCompanyChange = subCompany => {
        // Eğer "all" seçiliyse, her zaman ilk sırada olmalı olmadığında hata veriyor.
        if (subCompany && subCompany.includes("all")) {
            const allFirst = ["all", ...subCompany.filter(item => item !== "all")];
            setFilters(prev => ({...prev, subCompany: allFirst}));
            setPastShiftsFilters(prev => ({...prev, subCompany: allFirst}));
        } else {
            setFilters(prev => ({...prev, subCompany}));
            setPastShiftsFilters(prev => ({...prev, subCompany}));
        }
    };
    const onFutureSubCompanyChange = subCompany => {
        // Eğer "all" seçiliyse, her zaman ilk sırada olmalı olmadığında hata veriyor.
        if (subCompany && subCompany.includes("all")) {
            const allFirst = ["all", ...subCompany.filter(item => item !== "all")];
            setFutureShiftsFilters(prev => ({...prev, subCompany: allFirst}));
        } else {
            setFutureShiftsFilters(prev => ({...prev, subCompany}));
        }
    };
    const onStatusChange = status => {
        setFilters(prev => ({...prev, status}));
        setPastShiftsFilters(prev => ({...prev, status}));
        if (status && status !== "all") {
            setFilters(prev => ({...prev, currentStatus: "all"}));
            setPastShiftsFilters(prev => ({...prev, currentStatus: "all"}));
        }
        if (status !== "waitingApprove") {
            setIsWaitingApproveActive(false);
        }
    };

    return (
        <>
            <ShiftScoreModal
                visible={isModalOpen}
                onCancel={handleCancel}
                setIsModalOpen={setIsModalOpen}
            />
            <CustomerObjectionsModal
                visible={isObjectionsModalOpen}
                onCancel={handleObjectionsCancel}
            />
            <CustomerHomePageButtonContainer>
                <CustomerNavigationContainer>
                    <NavigationButtons
                        tabKey={tabKey}
                        changeTab={changeTab}
                        data={data}
                        futureData={futureData}
                        pastData={pastData}
                        count={count}
                        objectionsCount={objectionsCount}
                        setIsObjectionsModalOpen={setIsObjectionsModalOpen}
                        setApplyWaitingApproveFilters={setApplyWaitingApproveFilters}
                        isWaitingApproveActive={isWaitingApproveActive}
                        originalActiveData={originalActiveData}
                    />
                </CustomerNavigationContainer>
                <CustomerHomePageMapContainer>
                    <CustomerHomeMapComponent
                        selectedSubCompanyId={stores?.meStore?.selectedSubCompanyId || ""}
                    />
                </CustomerHomePageMapContainer>
            </CustomerHomePageButtonContainer>

            {/* Content based on active tab */}
            {tabKey === "activeShifts" && (
                <ActiveShifts
                    data={data}
                    setData={setData}
                    loading={loading}
                    filters={filters}
                    setFilters={setFilters}
                    hasMultipleSubcompanies={hasMultipleSubcompanies}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    reloadData={reloadData}
                    setReloadData={setReloadData}
                    reloadCount={reloadCount}
                    setReloadCount={setReloadCount}
                    onSubCompanyChange={onSubCompanyChange}
                    onStatusChange={onStatusChange}
                    showModal={showModal}
                    isWaitingApproveActive={isWaitingApproveActive}
                />
            )}
            {tabKey === "pastShifts" && (
                <PastShifts
                    pastShiftsFilters={pastShiftsFilters}
                    setPastShiftsFilters={setPastShiftsFilters}
                    hasMultipleSubcompanies={hasMultipleSubcompanies}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    reloadData={reloadData}
                    setReloadData={setReloadData}
                    reloadCount={reloadCount}
                    setReloadCount={setReloadCount}
                    onSubCompanyChange={onSubCompanyChange}
                    onStatusChange={onStatusChange}
                    pastLoading={pastLoading}
                    pastData={pastData}
                    setPastData={setPastData}
                />
            )}
            {tabKey === "futureShifts" && (
                <FutureShifts
                    futureShiftsFilters={futureShiftsFilters}
                    setFutureShiftsFilters={setFutureShiftsFilters}
                    hasMultipleSubcompanies={hasMultipleSubcompanies}
                    futureCurrentPage={futureCurrentPage}
                    setFutureCurrentPage={setFutureCurrentPage}
                    reloadData={reloadData}
                    setReloadData={setReloadData}
                    reloadCount={reloadCount}
                    setReloadCount={setReloadCount}
                    onFutureSubCompanyChange={onFutureSubCompanyChange}
                    futureLoading={futureLoading}
                    futureData={futureData}
                    setFutureData={setFutureData}
                />
            )}
        </>
    );
});

export default CustomerHomePageWithShiftScore;
